import React, { useState, useEffect } from "react";
import {
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  ThemeProvider,
  createTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

// Create a custom theme with the provided color palette
const theme = createTheme({
  palette: {
    primary: {
      main: "#3f434d", // P1
      light: "#63c2e6", // P2
    },
    secondary: {
      main: "#7cbf60", // S1
      light: "#f1a94e", // S2
    },
    neutral: {
      main: "#191b20", // N1
      dark: "#000", // N2
      light: "#fff", // N3
    },
    accent: {
      main: "#9d7fe9", // A1
      light: "#ff6b6b", // A2
    },
  },
});

const MonitoringSignup = ({
  open,
  onClose,
  monitoringId,
  existingData,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    street: "",
    city: "",
    state: "",
    zip: "",
    add_name: [""],
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (existingData) {
      setFormData({
        ...existingData,
        add_name: Object.values(JSON.parse(existingData.add_name)).filter(
          Boolean
        ),
      });
    }
  }, [existingData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleNicknameChange = (index, value) => {
    setFormData((prevData) => {
      const newAddName = [...prevData.add_name];
      newAddName[index] = value;
      return { ...prevData, add_name: newAddName };
    });
  };

  const addNickname = () => {
    if (formData.add_name.length < 5) {
      setFormData((prevData) => ({
        ...prevData,
        add_name: [...prevData.add_name, ""],
      }));
    }
  };

  const removeNickname = (index) => {
    setFormData((prevData) => {
      const newAddName = prevData.add_name.filter((_, i) => i !== index);
      return { ...prevData, add_name: newAddName };
    });
  };

  const validateForm = () => {
    const newErrors = {};

    // Name validations
    if (!formData.name.trim()) newErrors.name = "Full name is required";

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email))
      newErrors.email = "Invalid email address";

    // Phone number validation (simple format check)
    const phoneRegex = /^\d{10}$/;
    if (!phoneRegex.test(formData.phone))
      newErrors.phone = "Invalid phone number (10 digits required)";

    // Address validations
    if (!formData.street.trim())
      newErrors.street = "Street address is required";
    if (!formData.city.trim()) newErrors.city = "City is required";
    if (!formData.state.trim()) newErrors.state = "State is required";

    // Zip code validation
    const zipRegex = /^\d{5}(-\d{4})?$/;
    if (!zipRegex.test(formData.zip)) newErrors.zip = "Invalid ZIP code";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      setLoading(true);
      const dataToSubmit = {
        ...formData,
        monitoring_id: monitoringId,
        add_name: formData.add_name.reduce((acc, nick, index) => {
          if (nick) acc[`nick${index + 1}`] = nick;
          return acc;
        }, {}),
      };
      try {
        await onSave(dataToSubmit);
        onClose();
      } catch (error) {
        console.error("Error saving data:", error);
        // Optionally, you can set an error state and display it to the user
      } finally {
        setLoading(false);
      }
    } else {
      console.log("Form has errors, please correct them");
    }
  };

  // Common TextField style
  const textFieldStyle = {
    borderRadius: 4,
    backgroundColor: "neutral.light",
    "& .MuiOutlinedInput-root": {
      borderRadius: 2, // Adds border radius
      "&.Mui-focused fieldset": {
        borderColor: "primary.light",
      },
    },
  };

  return (
    <ThemeProvider theme={theme}>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle color="primary.main">
          {existingData ? "Edit Monitoring" : "Monitoring Signup"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, color: "neutral.main" }}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    error={!!errors.name}
                    helperText={errors.name}
                    sx={textFieldStyle}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    error={!!errors.phone}
                    helperText={errors.phone}
                    sx={textFieldStyle}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    error={!!errors.email}
                    helperText={errors.email}
                    sx={textFieldStyle}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Street Address"
                    name="street"
                    value={formData.street}
                    onChange={handleInputChange}
                    required
                    error={!!errors.street}
                    helperText={errors.street}
                    sx={textFieldStyle}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="City"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    required
                    error={!!errors.city}
                    helperText={errors.city}
                    sx={textFieldStyle}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="State"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    required
                    error={!!errors.state}
                    helperText={errors.state}
                    sx={textFieldStyle}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Zip"
                    name="zip"
                    value={formData.zip}
                    onChange={handleInputChange}
                    required
                    error={!!errors.zip}
                    helperText={errors.zip}
                    sx={textFieldStyle}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom color="secondary.main">
                    Additional Names (Nicknames)
                  </Typography>
                  {formData.add_name.map((nickname, index) => (
                    <Box
                      key={index}
                      sx={{ display: "flex", alignItems: "center", mb: 2 }}
                    >
                      <TextField
                        fullWidth
                        label={`Nickname ${index + 1}`}
                        value={nickname}
                        onChange={(e) =>
                          handleNicknameChange(index, e.target.value)
                        }
                        sx={textFieldStyle}
                      />
                      {index > 0 && (
                        <IconButton
                          onClick={() => removeNickname(index)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                  {formData.add_name.length < 5 && (
                    <Button
                      startIcon={<AddIcon />}
                      onClick={addNickname}
                      variant="outlined"
                      color="primary"
                      sx={{ mt: 1 }}
                    >
                      Add Nickname
                    </Button>
                  )}
                </Grid>
              </Grid>
            </form>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary">
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            sx={{
              bgcolor: loading ? "secondary.light" : "accent.main",
              "&:hover": {
                bgcolor: loading ? "secondary.light" : "accent.light",
              },
              color: "neutral.light",
            }}
          >
            {loading ? "Saving..." : existingData ? "Update" : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default MonitoringSignup;
